import platform from 'platform';

import { version } from '../package.json';
import { fetchConfiguration } from './api-clients';
import { BrowserSession } from './browser-session';
import buildConfig from './build-config.json';
import { EventChunkUploader } from './event-chunk-uploader';
import {
  CURRENT_SESSION_STARTED_AT_STORAGE_KEY,
  DEFAULT_INTERVAL_DAYS_TO_STORAGE_EXPIRATION,
  DEFAULT_SESSION_EXPIRY_INTERVAL,
  INVALID_STRING_VALUES,
  IS_FIRST_SESSION_STORAGE_KEY,
  LAST_EVENT_TRACKED_AT_STORAGE_KEY,
  STANDARD_EVENT_NAME,
  STANDARD_USER_PROFILE_NAME,
  SILVER_EGG_UID_STORAGE_KEY,
  USER_ID_LENGTH_MAX,
  USER_ID_STORAGE_KEY,
  DEFAULT_RECONFIGURE_INTERVAL,
  DEFAULT_CLOSE_MESSAGES_ON_PAGECHANGE,
  DEFAULT_REUSE_MESSAGES_ON_PAGECHANGE,
  REUSE_MESSAGES_ON_PAGECHANGE_VALUES,
  DEFAULT_CLOSE_MESSAGES_BY_IN_PAGE_LINK,
} from './constants';
import { CrossDomainHandler } from './cross-domain-handler';
import { ErrorNotifier } from './error-notifier';
import { buildCustomEvent } from './events/custom-event';
import { buildStandardEvent } from './events/standard-event';
import { STANDARD_EVENT_PROPERTY_KEYS } from './events/standard-event-properties';
import { MessageManager } from './message-manager';
import { BrowserCompatibility } from './browser-compatibility';
import { TrackingRule, buildTrackingRules } from './tracking-rule';
import { validateCustomUserProfile } from './user-profiles/custom-user-profile';
import { StandardUserProfile } from './user-profiles/standard-user-profile';
import { console as ReproConsole } from './utility/console';
import { formatISODate } from './utility/date';
import { EventLog } from './event-log';
import { storage } from './utility/storage';
import { Tracker, TrackerOptions, TrackerSetupOptions } from './tracker.types';
import { UserProfile } from './user-profiles/user-profile.types';
import { TriggeredEvent, TriggeredEventProperties } from './triggered-event.types';
import { Configuration } from './api-clients/configuration';
import { UserConsent, Consent } from './user-consent';
import { PushNotificationSubscriber } from './push-notification-subscriber';
import { PushNotificationEventProperties } from './types/push-notification.types';
import { Rethrower } from './utility/rethrower';
import { CustomHooks, HookType } from './hooks';
import { SpaMode } from './spamode';
import { fetchMessages } from './api-clients/message-list';
import { Idfv } from './idfv';
import { migrateIdfv } from './migrate-idfv';
import { UserProfileKeyNormalizer } from './name-normalizer';
import { isValidCookieDomain } from './utility/metadata';

const defaultTrackerSettings = {
  eventChunkUploadURL: buildConfig.eventChunkUploadURL,
  intervalDaysToStorageExpiration: DEFAULT_INTERVAL_DAYS_TO_STORAGE_EXPIRATION,
  sessionExpiryInterval: DEFAULT_SESSION_EXPIRY_INTERVAL,
  reconfigureInterval: DEFAULT_RECONFIGURE_INTERVAL,
  writeLog: false,
  snippetVersion: '',
  token: '',
};

const defaultOptions: TrackerOptions = {
  allow_webview: false,
  dangerously_bypass_browser_check: false,
  interval_days_to_storage_expiration: DEFAULT_INTERVAL_DAYS_TO_STORAGE_EXPIRATION,
  session_expiry_interval: DEFAULT_SESSION_EXPIRY_INTERVAL,
  log_level: 'default',
  opt_out_by_default: false,
  linker_domains: [] as string[],
  disable_auto_attach_linker_params: false,
  disable_linker_params_referrer_check: false,
  disable_ip_list: [] as string[],
  silver_egg_uid_storage_key: '',
  spa_mode: SpaMode.NONE as SpaMode,
  write_log: true,
  close_messages_on_pagechange: DEFAULT_CLOSE_MESSAGES_ON_PAGECHANGE,
  reuse_messages_on_pagechange: DEFAULT_REUSE_MESSAGES_ON_PAGECHANGE,
  close_messages_by_in_page_link: DEFAULT_CLOSE_MESSAGES_BY_IN_PAGE_LINK,
  cookie_domain: undefined,
};

const PATH_NORMALIZATION_REGEX = /\/\/+/g;

declare const gtag: Function;
declare const ga: Function;

// TODO: Symbol などを利用してアプリ事業者が直接呼ばないであろうメソッドを private 化する。まずは `_` をつけて対応
export class DefaultTracker implements Tracker {
  private static isInitalizedOnSpaSite = false;

  public eventLog: EventLog | null;
  private settings: typeof defaultTrackerSettings;
  private userAnnotation: string;
  private idfv: string;
  private profileQueue: UserProfile[];
  private eventQueue: TriggeredEvent[];
  private trackingRules: TrackingRule[];
  private configured: boolean;
  private messagesFetched: boolean;
  private _messageManager: MessageManager;
  private eventChunkUploader: EventChunkUploader;
  private _crossDomainHandler: CrossDomainHandler;
  private _pushNotificationSubscriber: PushNotificationSubscriber | null = null;
  private session: BrowserSession;
  private isBrowserSupported: boolean;
  private userConsent: Consent;
  private spaMode = SpaMode.NONE;
  private hooks = CustomHooks.create();
  // @ts-ignore: Will not be used until https://github.com/reproio/web-sdk/pull/1166/ is merged
  private gaTrackingCode = '';
  private options: TrackerOptions = defaultOptions;
  private lastSetupOnSpaSite: number | null = null;

  public constructor() {
    this.settings = defaultTrackerSettings;
    this.userAnnotation = '';
    this.idfv = '';
    this.profileQueue = [];
    this.eventQueue = [];
    this.trackingRules = [];

    this.configured = false;
    this.messagesFetched = false;
    this._messageManager = new MessageManager();
    this.eventChunkUploader = new EventChunkUploader();
    this._crossDomainHandler = new CrossDomainHandler(this);
    this.session = new BrowserSession({
      idfv: this.idfv,
    });
    this.eventLog = null;

    this.isBrowserSupported = BrowserCompatibility.isSupported({
      name: platform.name,
      version: platform.version,
      osFamily: platform.os && platform.os.family,
      osVersion: platform.os && platform.os.version,
      userAgent: navigator.userAgent,
    });
    this.userConsent = UserConsent.create(storage);

    this._attachPageShowEvent();
  }

  public isPushNotificationSubscribeAllowed(): boolean {
    if (!this.isAllowedTracking) return false;
    if (this._pushNotificationSubscriber == null) return false;

    return this._pushNotificationSubscriber.isSubscribeAllowed;
  }

  public subscribeToPushNotification(): void {
    if (!this.isAllowedTracking) return;
    if (this._pushNotificationSubscriber == null) return;

    this._pushNotificationSubscriber.subscribe();
  }

  public unsubscribeFromPushNotification(): void {
    if (!this.isAllowedTracking) return;
    if (this._pushNotificationSubscriber == null) return;

    this._pushNotificationSubscriber.unsubscribe();
  }

  public buildDefaultProperties(): TriggeredEventProperties {
    return {
      url: window.location.href,
      path: this.getHashRouterCompatiblePath(),
      query: window.location.search,
      referrer: document.referrer,
      title: document.title,
    };
  }

  public setSnippetVersion(ver: string): void {
    this.settings.snippetVersion = ver;
  }

  private get isAllowedTracking(): boolean {
    return this.userConsent.isOptedIn() && this.isBrowserSupported;
  }

  // GDPRに関する、ユーザー情報を消すためにSupplierがユーザーを特定できるAPIです 詳細は：https://repro.esa.io/posts/19163
  public getDeviceID(): string | null {
    return this.idfv;
  }

  public optIn(): boolean {
    if (!this.isBrowserSupported) return false;
    return this.userConsent.optIn({
      intervalDaysToStorageExpiration: this.settings.intervalDaysToStorageExpiration,
      cookieDomain: this.options.cookie_domain,
    });
  }

  public optOut(): boolean {
    if (!this.isBrowserSupported) return false;
    const ret = this.userConsent.optOut({
      intervalDaysToStorageExpiration: this.settings.intervalDaysToStorageExpiration,
      cookieDomain: this.options.cookie_domain,
    });

    if (!ret) {
      return false;
    }

    this._reset();
    return true;
  }

  public isOptedIn(): boolean {
    return this.userConsent.isOptedIn();
  }

  public isSubscribedToPushNotification(): Promise<boolean> {
    if (!this._pushNotificationSubscriber) return Promise.resolve(false);

    return this._pushNotificationSubscriber.isSubscribed();
  }

  public async setup(token: string, options: TrackerSetupOptions = {}): Promise<boolean> {
    // Prevent running setup if there is an active configuration
    // i.e. setup() is called twice in a row without setUserID() which resets the tracker
    if (this.configured) {
      return false;
    }

    if (!token) {
      ReproConsole.error('Aborting setup process: No token set.');
      return false;
    }

    this.settings.token = token;

    this.populateOptions(options);
    this.isBrowserSupported = this._confirmRunOnSupportedBrowser({ bypass_webview_check: true }); // NOTE: webview check will be performed after getting config from server.
    // TODO:: remote setup での webview 設定を有効にするためには、ここでは bypass した結果を使ってあとでもう一度チェックする必要がある。
    // しかし、アクセス増がどの程度なのかわからないため今は一旦 webview の場合ここで止める事にする。問題なさそうなことが確認できたあとでこのチェックブロックごと消すこと。
    // https://github.com/reproio/web-sdk/pull/1441/files#r934138345
    if (
      !this.options.allow_webview &&
      BrowserCompatibility.isWebView({ name: platform.name, userAgent: navigator.userAgent })
    ) {
      // TODO: どのくらい webview チェックで落ちているのかを計測したい
      return false;
    }
    if (!this.isBrowserSupported) return false;

    return this.setupInternal();
  }

  public on(type: HookType | HookType[], callback: () => void) {
    const eventTypes = Array.isArray(type) ? type : [type];
    eventTypes.forEach((eventType) => {
      this.hooks.on(eventType, callback);
    });
  }

  /**
   * @deprecated use `on` method.
   */
  public event(type: HookType | HookType[], callback: () => void) {
    ReproConsole.warn(`[DEPRECATED] reproio('event') has been deprecated. Use reproio('on') instead.`);
    return this.on(type, callback);
  }

  public off(type: HookType | HookType[], callback: () => void) {
    (Array.isArray(type) ? type : [type]).forEach((eventType) => {
      this.hooks.off(eventType, callback);
    });
  }

  private populateOptions(options: TrackerSetupOptions = {}): void {
    // Note: Current implementation read this.options and this.settings directly from each methods.
    //       We probably should change this structure in future refactor.
    const mergedOptions = {
      ...this.options,
      ...options,
    };

    this.settings.intervalDaysToStorageExpiration = mergedOptions.interval_days_to_storage_expiration;
    this.settings.sessionExpiryInterval = mergedOptions.session_expiry_interval;
    this.settings.writeLog = mergedOptions.write_log;

    this._messageManager.defaultCloseOnPagechange = mergedOptions.close_messages_on_pagechange;

    if (REUSE_MESSAGES_ON_PAGECHANGE_VALUES.includes(mergedOptions.reuse_messages_on_pagechange)) {
      this._messageManager.reuseMessagesOnPagechange = mergedOptions.reuse_messages_on_pagechange;
    } else {
      ReproConsole.error(
        `Invalid value for reuse_messages_on_pagechange (${mergedOptions.reuse_messages_on_pagechange}) has been ignored.`
      );
    }

    this.spaMode = mergedOptions.spa_mode;

    // TODO: 連携先が複数増えそうになるタイミングでリファクタして if 文を滅ぼす
    if (mergedOptions.silver_egg_uid_storage_key) {
      storage.set(SILVER_EGG_UID_STORAGE_KEY, mergedOptions.silver_egg_uid_storage_key, {
        expires: this.settings.intervalDaysToStorageExpiration,
      });
    } else {
      storage.remove(SILVER_EGG_UID_STORAGE_KEY);
    }

    ReproConsole.memorizeLogLevel(mergedOptions.log_level);

    const { cookie_domain } = mergedOptions;
    if (cookie_domain && !isValidCookieDomain(cookie_domain)) {
      ReproConsole.error(`Invalid cookie_domain value: ${cookie_domain}`);
      mergedOptions.cookie_domain = undefined;
    }

    this.options = mergedOptions;
  }

  private async setupInternal(): Promise<boolean> {
    if (
      this.userConsent.isImplicitlyOptedOut({
        optOutByDefault: this.options.opt_out_by_default,
      })
    ) {
      this.userConsent.markAsExplicitlyOptedOut({
        intervalDaysToStorageExpiration: this.settings.intervalDaysToStorageExpiration,
        cookieDomain: this.options.cookie_domain,
      });
      this.profileQueue = [];
      this.eventQueue = [];
      return false;
    }

    this.userConsent.updateExpiration({
      intervalDaysToStorageExpiration: this.settings.intervalDaysToStorageExpiration,
      cookieDomain: this.options.cookie_domain,
    });

    if (!this.userConsent.isOptedIn()) return false;

    const now = new Date();

    if (
      !this._configureLinkerDomain({
        linkerDomains: this.options.linker_domains,
        disableLinkerParamsReferrerCheck: this.options.disable_linker_params_referrer_check,
        now,
      })
    ) {
      return false;
    }

    // userAnnotation & idfv決定後に設定されるべき
    this.eventLog = new EventLog({
      userIdentifier: this.userIdentifier,
      isWriteAllowed: this.options.write_log,
    });

    return this.configure({ now });
  }

  private _confirmRunOnSupportedBrowser({
    bypass_webview_check = false,
  }: { bypass_webview_check?: boolean } = {}): boolean {
    // 原則としてサポート対象外のブラウザでは SDK を動作させないが特定の option が渡された場合にかぎり動作を許可する
    if (!this.options.dangerously_bypass_browser_check && !this.isBrowserSupported) {
      return false;
    }

    if (!bypass_webview_check) {
      const allowWebView = this.options.allow_webview;
      if (
        !allowWebView &&
        BrowserCompatibility.isWebView({
          name: platform.name,
          userAgent: navigator.userAgent,
        })
      ) {
        return false;
      }
    }

    // dangerously_bypass_browser_check: true が渡された場合は、サポート対象外のブラウザであっても動作させるため
    // this.isBrowserSupported を true に更新する
    return true;
  }

  private _configureLinkerDomain({
    linkerDomains,
    disableLinkerParamsReferrerCheck,
    now,
  }: {
    linkerDomains: string[];
    disableLinkerParamsReferrerCheck: boolean;
    now: Date;
  }): boolean {
    const searchParams = new URLSearchParams(window.location.search).get('rpr') || '';
    let isFromSpecifiedDomain = false;
    let isValidSessionParamFormat = false;
    let isSessionInheritable = false;

    if (linkerDomains) {
      if (!this._crossDomainHandler.verifyLinkerDomains(linkerDomains)) {
        // Abort setup if invalid domains are passed to prevent inaccurate tracking & help Suppliers detect mistakes when using the cross-domain feature
        ReproConsole.error('Aborting setup: Invalid linker domain detected.');
        return false;
      }
      this._crossDomainHandler.setLinkerDomains(linkerDomains);

      // Checking document.referrer prevents other users from inheriting the same session if the link with parameters is shared.
      // Because some sites have disabled document.referrer from being passed, the `disable_linker_params_referrer_check` option allows Suppliers to bypass this check
      if (disableLinkerParamsReferrerCheck) {
        isFromSpecifiedDomain = true;
      } else {
        try {
          const referrerHostname = new URL(document.referrer).hostname;
          isFromSpecifiedDomain = this._crossDomainHandler.isLinkerDomain(referrerHostname);
        } catch (err) {}
      }

      if (searchParams) {
        isValidSessionParamFormat = this._crossDomainHandler.verifySessionParametersFormat(searchParams);
        if (!isValidSessionParamFormat) return false;

        // Do not allow session to be inheritted if the session is too old
        // This is mostly to prevent other users from inheriting another user's session when the `disable_linker_params_referrer_check` option is used
        isSessionInheritable = this._crossDomainHandler.verifySessionInheritability(searchParams);
      }
    }

    try {
      if (isFromSpecifiedDomain && isSessionInheritable) {
        const [idfv, lastEventTrackedAt, firstSessionValue] = searchParams.split(',');

        this.idfv = idfv;
        this.session = new BrowserSession({
          idfv,
          currentDate: now,
          firstSessionValue: {
            ...storage.get(IS_FIRST_SESSION_STORAGE_KEY, { asJSON: true }),
            ...{ [idfv]: parseInt(firstSessionValue, 10) },
          },
          lastEventTrackedAt: new Date(parseInt(lastEventTrackedAt, 10)),
          intervalDaysToStorageExpiration: this.settings.intervalDaysToStorageExpiration,
          sessionExpiryInterval: this.settings.sessionExpiryInterval,
          cookieDomain: this.options.cookie_domain,
        });
      } else {
        const idfv = new Idfv();
        this.idfv = idfv.value;

        // 複数タブで同時にWeb SDKの入ったページを開き、新規ユーザーとしての処理が始まると
        // それぞれのタブで一時的にidfvを生成して利用されてしまい、subscriptionとidfvの対応関係が壊れてしまうので
        // uuidを生成しthis.idfvとして保持する時点で永続化して別のタブでも同一のidfvが読めるようにする
        storage.set(USER_ID_STORAGE_KEY, this.idfv, {
          expires: this.settings.intervalDaysToStorageExpiration,
          domain: this.options.cookie_domain,
        });

        if (!idfv.isValid()) {
          const oldIdfv = idfv;
          const newIdfv = idfv.recover();
          migrateIdfv(this.settings.token, idfv.value, newIdfv.value, {
            intervalDaysToStorageExpiration: this.settings.intervalDaysToStorageExpiration,
            cookieDomain: this.options.cookie_domain,
          });
          this.idfv = newIdfv.value;

          ErrorNotifier.notify({
            error: new Error('Broken idfv detected'),
            extraPayload: { oldIdfv: oldIdfv.value, newIdfv: newIdfv.value, token: this.settings.token },
            samplingRatio: 10000,
          });
        }

        // TODO: https://github.com/reproio/web-sdk/pull/372#discussion_r290548249
        const lastEventTrackedAt = storage.get(LAST_EVENT_TRACKED_AT_STORAGE_KEY)
          ? new Date(parseInt(storage.get(LAST_EVENT_TRACKED_AT_STORAGE_KEY), 10))
          : undefined;

        this.session = new BrowserSession({
          idfv: this.idfv,
          currentDate: now,
          firstSessionValue: storage.get(IS_FIRST_SESSION_STORAGE_KEY, { asJSON: true }),
          lastEventTrackedAt,
          intervalDaysToStorageExpiration: this.settings.intervalDaysToStorageExpiration,
          sessionExpiryInterval: this.settings.sessionExpiryInterval,
          cookieDomain: this.options.cookie_domain,
        });
      }
    } catch (err: any) {
      this._log('error', err);
      return false;
    }

    return true;
  }

  // TODO: ちゃんと設計を修正する
  // Unit testのために_trackByTrackingRuleを外部から呼ぶ必要があるが、privateメソッドなので避けたい
  // 暫定的にprivateメソッドへのアクセスを回避するためのwrapperメソッド
  public trackByTrackingRules__ForTesting(loc: Location): void | false {
    return this._trackByTrackingRules(loc);
  }

  private _trackByTrackingRules(loc: Location = window.location): void | false {
    if (!this.isAllowedTracking) return false;

    // trackingRule によって指定された条件に合致するイベントはすべてトラックして良い
    this.trackingRules.forEach((trackingRule) => {
      if (trackingRule.isAnyofTriggersSatisfied(loc)) {
        const event = buildCustomEvent(trackingRule.eventName, {});
        if (!event) return;
        this._trackEvent(event);
      }
    });
  }

  private async _fetchConfiguration() {
    return fetchConfiguration({
      userAnnotation: this.userAnnotation,
      idfv: this.idfv,
      token: this.settings.token,
    });
  }

  private async configure({ now }: { now: Date }): Promise<boolean> {
    try {
      const configuration = await Rethrower.try(async () => this._fetchConfiguration());
      this.gaTrackingCode = configuration.googleAnalyticsTrackingCode;

      this.configured = true;
      ErrorNotifier.init(configuration.send_errors, {
        appId: configuration.appId,
        userAnnotation: this.userAnnotation,
        idfv: this.idfv,
        isFirstSession: this.session.isFirstVisit(),
      });

      return this._runPostConfiguration({ now, configuration });
    } catch (error) {
      if (error && error instanceof Rethrower) {
        this.configured = false;
        ReproConsole.error(error.error);
        return false;
      }
      throw error;
    } finally {
      // セッション情報の更新は Configuration への通信の可否によらず実施する
      this.saveSession();
    }
  }

  private async _runPostConfiguration({
    now,
    configuration,
  }: {
    now: Date;
    configuration: Configuration;
  }): Promise<boolean> {
    if (configuration.setupOptions) {
      this.populateOptions(configuration.setupOptions);
      this.isBrowserSupported = this._confirmRunOnSupportedBrowser();
      if (!this.isBrowserSupported) {
        this.eventQueue = [];
        this.profileQueue = [];
        return false;
      }
    }

    if (configuration.setupOptions?.interval_days_to_storage_expiration) {
      this.userConsent.updateExpiration({
        intervalDaysToStorageExpiration: this.settings.intervalDaysToStorageExpiration,
        cookieDomain: this.options.cookie_domain,
      });
      this.session.intervalDaysToStorageExpiration = this.settings.intervalDaysToStorageExpiration;
    }

    if (typeof configuration.setupOptions?.session_expiry_interval === 'number') {
      this.session.sessionExpiryInterval = configuration.setupOptions.session_expiry_interval;
    }

    // ブラウザのサポート是非にかかわらず Configuration API から返ってきたレスポンスで
    // trackable: false が含まれる場合は Public API が利用できてはならない
    if (!configuration.trackable) {
      this.isBrowserSupported = false;
      this.eventQueue = [];
      this.profileQueue = [];
      return false;
    }

    if (configuration.googleAnalyticsTrackingCode) {
      this.initializeGoogleAnalyticsForPrecacher(configuration);
    }

    if (this.options.disable_ip_list.includes(configuration.ipAddress)) {
      ReproConsole.info('stop tracking due to disable_ip_list.');
      return false;
    }

    // trackingRules 自体は configuration からもらった時点で Tracker に持たせて良いが、
    // _trackByTrackingRules() の呼び出しは、それにより発火するイベントがトリガとなるメッセージがあった
    // ときのために MessageManager#buildMessages() のあとに呼ぶ必要がある
    if (Object.keys(configuration.trackingRules).length > 0) {
      this.trackingRules = buildTrackingRules(configuration.trackingRules);
    }

    this.settings.reconfigureInterval = configuration.reconfigureInterval;

    const shouldLaunchInspector =
      configuration.inspect_mode != null && configuration.inspect_mode.idfv.split(',').includes(this.idfv);
    this.initEventChunkUploader({ configuration, shouldLaunchInspector });
    this.initPushNotification({ configuration });

    this._messageManager.activate({
      token: this.settings.token,
      idfv: this.idfv,
      userAnnotation: this.userAnnotation,
      eventChunkUploader: this.eventChunkUploader,
      eventLog: this.eventLog,
    });

    // Wait for messages to build before tracking events to ensure messages are shown
    try {
      await Rethrower.try(async () => {
        const messages = await fetchMessages({
          userAnnotation: this.userAnnotation,
          idfv: this.idfv,
          isFirstSession: this.session.isFirstVisit(),
          token: this.settings.token,
        });

        this._messageManager.buildMessages({
          messages,
          silverEggSetting: configuration.silverEggSetting,
          onEventTrack: (eventName: string) => this.track(eventName),
        });
      });

      this.messagesFetched = true;
    } catch (error) {
      if (error && error instanceof Rethrower) {
        ReproConsole.error(error.error);
      } else {
        throw error;
      }
    }

    if (!this.session.isContinuous(now)) {
      await Rethrower.try(() => this._trackNewSession({ trackedAt: now }));
      // eventChunkUploaderで送信する基本情報はsession開始時のみ
      this._setLastSessionDate();
      this._setLocale();
      this._setTimeZone();
    }

    // spa_mode がオンのときに、configuration 実施 と pushState or hashchange による
    // ページ遷移が同じタイミングで発生すると二重に _trackByTrackingRules を発火してしまうので
    // config 実施後のトラッキングは isInitalizedOnSpaSite をチェックしてこれを防ぐ
    if (!DefaultTracker.isInitalizedOnSpaSite) {
      this._trackByTrackingRules();
    }
    // queueにたまったeventやuserProfileなどのeventChunkをまとめてアップロード
    this._setQueuedUserProfiles();
    await this._trackQueuedEvents(now);

    if (this._crossDomainHandler.isActive) {
      this.updateLinkerParameters();
      this._crossDomainHandler.addMutationObserver(this.options.disable_auto_attach_linker_params);
    }

    // SpaモードがONの際は初回ロード時にinitイベントを発火する
    if (this.spaMode !== SpaMode.NONE && !DefaultTracker.isInitalizedOnSpaSite) {
      this.markInitializeCompleteOnSpaSite();
      this.hooks.emit(HookType.INIT);
      this.on(HookType.PAGECHANGE, async () => {
        await this.throttledSetUpInternalOnPageChange(this.options.disable_auto_attach_linker_params);
        this._trackByTrackingRules();
      });
    }

    return true;
  }

  private markInitializeCompleteOnSpaSite() {
    DefaultTracker.isInitalizedOnSpaSite = true;
    this.setLastSetupTimeOnSpaSite();
  }

  private setLastSetupTimeOnSpaSite() {
    this.lastSetupOnSpaSite = Date.now();
  }

  private reconfigureIntervalTimeElapsed() {
    return this.lastSetupOnSpaSite != null && this.lastSetupOnSpaSite <= Date.now() - this.settings.reconfigureInterval;
  }

  private async throttledSetUpInternalOnPageChange(disableAutoAttachLinkerParams: boolean): Promise<void> {
    if (!this.reconfigureIntervalTimeElapsed()) {
      return;
    }
    await this.reconfigure(disableAutoAttachLinkerParams);
  }

  private async reconfigure(disableAutoAttachLinkerParams?: boolean) {
    this._messageManager.clearCurrentlyDisplayableMessages();
    this._messageManager.markBuildMessageComplete();
    this.updateLinkerParameters();
    this._crossDomainHandler.addMutationObserver(
      disableAutoAttachLinkerParams ?? this.options.disable_auto_attach_linker_params
    );

    this.setLastSetupTimeOnSpaSite();
    await this.setupInternal();
  }

  private saveSession() {
    this.session.save();
  }

  private initEventChunkUploader({
    configuration,
    shouldLaunchInspector,
  }: {
    configuration: Configuration;
    shouldLaunchInspector: boolean;
  }) {
    this.eventChunkUploader = new EventChunkUploader({
      uploadable: configuration.trackable,
      uploadURL: this.settings.eventChunkUploadURL,
      insightId: configuration.insightId,
      idfv: this.idfv,
      userAnnotation: this.userAnnotation,
      snippetVersion: this.settings.snippetVersion,
      timeOffsetMills: configuration.timeOffsetMills,
      ipAddress: configuration.ipAddress,
      uploadInterval: configuration.dataUploadInterval,
      token: this.settings.token,
      shouldLaunchInspector,
      eventQueue: this.eventQueue,
      setupOptions: this.options,
    });
  }

  private initPushNotification({ configuration }: { configuration: Configuration }): void {
    if (configuration.pushSetting == null) return;

    this._pushNotificationSubscriber = new PushNotificationSubscriber({
      onSubscribeSucceed: (subscription: PushSubscriptionJSON) => this._saveSubscription(subscription),
      onPushShow: (tracked_at: Date, properties: PushNotificationEventProperties) =>
        this._trackPushShow(tracked_at, { ...properties }),
      onPushClick: (tracked_at: Date, properties: PushNotificationEventProperties) =>
        this._trackPushClick(tracked_at, { ...properties }),
      onPushDataFetched: (properties: PushNotificationEventProperties) => this._trackPushDataFetched({ ...properties }),
    });

    this._pushNotificationSubscriber.setRegistrationOptions(configuration.pushSetting);

    if (PushNotificationSubscriber.isPermissionGranted) {
      // iOS Safari ではユーザーの操作によるイベントにフックさせないと PushManager.subscribe()
      // が呼べない。基本的には初回 subscribe 時の subscription 取得 + 即時アップロードによって
      // 正しく送信できているケースが多いはずだが、そのタイミングでの送信に失敗すると以後送れるタイミングがなくなってしまう。
      // body クリックにフックさせて subscribe を呼び直すワークアラウンドでこの問題を回避する。
      document.body.addEventListener(
        'click',
        () => {
          this._pushNotificationSubscriber?.subscribe();
        },
        { once: true }
      );
    }
  }

  private async _trackQueuedEvents(date: Date): Promise<void> {
    if (!this.isAllowedTracking) return;

    if (this.eventQueue.length > 0) {
      this.session.extendExpiration(date);
      this.saveSession();
    }

    await this.eventQueue.reduce((chainedPromise: Promise<void>, event) => {
      if (this.eventLog) this.eventLog.append(event);
      this.eventChunkUploader.addEvent(event);

      return chainedPromise.then(async () => {
        await this._messageManager.showTriggeredMessages(event);
      });
    }, Promise.resolve());

    this.eventQueue = [];
  }

  public track(name: string, properties = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildCustomEvent(name, properties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackViewContent(content_id: string, optionalProperties: { [key: string]: any } = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('VIEW_CONTENT', { content_id }, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackSearch(optionalProperties: { [key: string]: any } = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('SEARCH', {}, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackAddToCart(content_id: string, optionalProperties: { [key: string]: any } = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('ADD_TO_CART', { content_id }, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackAddToWishlist(optionalProperties: { [key: string]: any } = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('ADD_TO_WISHLIST', {}, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackInitiateCheckout(optionalProperties: { [key: string]: any } = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('INITIATE_CHECKOUT', {}, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackAddPaymentInfo(optionalProperties: { [key: string]: any } = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('ADD_PAYMENT_INFO', {}, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackPurchase(
    content_id: string,
    value: number,
    currency: number,
    optionalProperties: { [key: string]: any } = {}
  ): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('PURCHASE', { content_id, value, currency }, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackShare(optionalProperties: { [key: string]: any } = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('SHARE', {}, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackLead(optionalProperties: { [key: string]: any } = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('LEAD', {}, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  public trackCompleteRegistration(optionalProperties: { [key: string]: any } = {}): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('COMPLETE_REGISTRATION', {}, optionalProperties);
    if (!event) return false;

    this._trackEvent(event);
  }

  private _trackPushShow(tracked_at: Date, requiredProperties: PushNotificationEventProperties): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('SHOW_WEB_PUSH_NOTIFICATION', { ...requiredProperties }, {});
    if (!event) return false;

    // 現在時刻ではなく ServiceWorker でイベントを記録した時刻で tracked_at を上書きする
    event.tracked_at = tracked_at;
    this._trackEvent(event, { canStartSession: false });
  }

  private _trackPushClick(tracked_at: Date, requiredProperties: PushNotificationEventProperties): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('CLICK_WEB_PUSH_NOTIFICATION', { ...requiredProperties }, {});
    if (!event) return false;

    // 現在時刻ではなく ServiceWorker でイベントを記録した時刻で tracked_at を上書きする
    event.tracked_at = tracked_at;
    this._trackEvent(event, { canStartSession: false });
  }

  private _trackPushDataFetched(requiredProperties: PushNotificationEventProperties): void | false {
    if (!this.isAllowedTracking) return false;

    const event = buildStandardEvent('FETCH_WEB_PUSH_NOTIFICATION_DATA', { ...requiredProperties }, {});
    if (!event) return false;

    this._trackEvent(event, { canStartSession: false });
  }

  private _saveSubscription(subscription: PushSubscriptionJSON): void | false {
    if (!this.isAllowedTracking) return false;

    this.eventChunkUploader.setSubscription(subscription);
  }

  private _trackEvent(originalEvent: TriggeredEvent, { canStartSession = true } = {}): void | false {
    const event: TriggeredEvent = {
      ...originalEvent,
      properties: {
        ...this.buildDefaultProperties(),
        ...originalEvent.properties,
      },
    };

    // GDPR対応のため、opted-inでなければstorageへ書き込んではならない
    // また、メッセージ一覧が取得できていない状態では、取得できるまでeventQueueに貯めておく
    // ref. https://github.com/reproio/web-sdk/pull/1120
    if (!this.configured || !this.messagesFetched) {
      // eventQueueとeventLogは統合できる気がする
      this.eventQueue.push(event);
      return false;
    }

    // 本当はappend完了を待ってから続きを処理したいが
    // そのためにはtrackのpromise化にまで変更が波及する
    if (this.eventLog) this.eventLog.append(event);

    if (canStartSession && !this.session.isContinuous(event.tracked_at)) {
      this._trackNewSession({ trackedAt: event.tracked_at });
    }

    // To ensure sessionManager is updated and avoid multiple new session triggers
    this.session.extendExpiration(event.tracked_at);
    this.saveSession();
    // Update linker params since it contains rpr_event_last_tracked_at
    // which becomes outdataed after tracking an event
    if (this._crossDomainHandler.isActive) {
      this.updateLinkerParameters();
    }

    if (this._messageManager.buildMessagesComplete) {
      this._messageManager.showTriggeredMessages(event);
    }

    this.eventChunkUploader.addEvent(event);
  }

  private async _trackNewSession({ trackedAt }: { trackedAt: Date }): Promise<void | false> {
    if (!this.isAllowedTracking) return false;

    const event: TriggeredEvent = {
      name: STANDARD_EVENT_NAME('session_start'),
      properties: this.buildDefaultProperties(),
      tracked_at: trackedAt,
      metadata: {},
    };

    if (this.eventLog) this.eventLog.append(event);

    this.session.start(event.tracked_at);
    this.session.extendExpiration(event.tracked_at);
    this.saveSession();

    this._messageManager.showTriggeredMessages(event);
    this.eventChunkUploader.addEvent(event, true);
    return Promise.resolve();
  }

  private getHashRouterCompatiblePath(url: Location = window.location): string {
    if (this.spaMode === SpaMode.HASH) {
      return `${url.pathname}/${url.hash}`.replace(PATH_NORMALIZATION_REGEX, '/');
    }
    return url.pathname;
  }

  public setUserID(userId: string): void | false {
    if (!this.isBrowserSupported) return false;
    if (!this._validateUserId(userId)) return false;

    if (this.userAnnotation === userId) return;

    if (this.configured) {
      this._reset();
    }
    this.userAnnotation = userId;
  }

  public version() {
    return version;
  }

  private async _reset() {
    this.eventQueue = [];
    this.trackingRules = [];
    this.configured = false;
    this.messagesFetched = false;
    this._messageManager.clearCurrentlyDisplayableMessages();
    this._messageManager.clearShownMessages();
    this.session.revoke();
    await this.eventChunkUploader.reset();
    this.unsubscribeFromPushNotification();
    this._crossDomainHandler.reset();
  }

  private _setQueuedUserProfiles(): void {
    if (!this.isAllowedTracking) return;

    this.profileQueue.forEach(({ type, key, value }) => {
      this._executeSetUserProfile({ type, key, value });
    });

    this.profileQueue = [];
  }

  private _setLastSessionDate(): void | false {
    if (!this.isAllowedTracking) return false;

    const sessionStartedAt = storage.get(CURRENT_SESSION_STARTED_AT_STORAGE_KEY);
    if (!sessionStartedAt || !parseInt(sessionStartedAt, 10)) {
      ReproConsole.info('The Last Session Date User Profile could not be set.');
      return false;
    }

    this.eventChunkUploader.setUserProfile({
      type: 'datetime',
      key: STANDARD_USER_PROFILE_NAME('last_session_date'),
      value: formatISODate(new Date(parseInt(sessionStartedAt, 10))),
    });
  }

  private _setLocale(): void | false {
    if (!this.isAllowedTracking) return false;

    if (!('Intl' in window)) {
      ReproConsole.info('The Locale User Profile could not be set. The required API is missing.');
      return false;
    }

    this.eventChunkUploader.setUserProfile({
      type: 'string',
      key: STANDARD_USER_PROFILE_NAME('locale'),
      value: new Intl.DateTimeFormat().resolvedOptions().locale,
    });
  }

  private _setTimeZone(): void | false {
    if (!this.isAllowedTracking) return false;

    if (!('Intl' in window)) {
      ReproConsole.info('The Time Zone User Profile could not be set. The required API is missing.');
      return false;
    }

    const { timeZone } = new Intl.DateTimeFormat().resolvedOptions();
    if (timeZone == null) {
      ReproConsole.info('The Time Zone User Profile could not be set. Time zone cannot be detected.');
      return false;
    }

    this.eventChunkUploader.setUserProfile({
      type: 'string',
      key: STANDARD_USER_PROFILE_NAME('time_zone'),
      value: timeZone,
    });
  }

  // TODO: 型が違う場合の例外処理
  public setStringUserProfile(key: string, value: string): void | false {
    if (!this.isAllowedTracking) return false;

    if (!validateCustomUserProfile('string', key, value)) return false;

    return this._setUserProfile({ type: 'string', key, value });
  }

  public setIntUserProfile(key: string, value: any): void | false {
    if (!this.isAllowedTracking) return false;

    if (!validateCustomUserProfile('int', key, value)) return false;

    return this._setUserProfile({ type: 'int', key, value });
  }

  public setDoubleUserProfile(key: string, value: any): void | false {
    if (!this.isAllowedTracking) return false;

    if (!validateCustomUserProfile('decimal', key, value)) return false;

    return this._setUserProfile({ type: 'decimal', key, value });
  }

  public setDateUserProfile(key: string, value: Date): void | false {
    if (!this.isAllowedTracking) return false;
    if (!validateCustomUserProfile('datetime', key, value)) return false;

    const formatted = formatISODate(value);

    return this._setUserProfile({ type: 'datetime', key, value: formatted });
  }

  public setUserGender(value: string): void | false {
    if (!this.isAllowedTracking) return false;
    if (!StandardUserProfile.validateGender(value)) return false;

    return this._setUserProfile({ type: 'string', key: STANDARD_USER_PROFILE_NAME('gender'), value });
  }

  public setUserEmailAddress(value: string): void | false {
    if (!this.isAllowedTracking) return false;
    if (!StandardUserProfile.validateEmail(value)) return false;

    return this._setUserProfile({ type: 'string', key: STANDARD_USER_PROFILE_NAME('email_address'), value });
  }

  public setUserResidencePrefecture(value: string): void | false {
    if (!this.isAllowedTracking) return false;
    if (!StandardUserProfile.validatePrefecture(value)) return false;

    return this._setUserProfile({ type: 'string', key: STANDARD_USER_PROFILE_NAME('residence_prefecture'), value });
  }

  public setUserDateOfBirth(value: Date): void | false {
    if (!this.isAllowedTracking) return false;
    if (!validateCustomUserProfile('datetime', 'date_of_birth', value)) return false;

    return this._setUserProfile({
      type: 'datetime',
      key: STANDARD_USER_PROFILE_NAME('date_of_birth'),
      value: formatISODate(value),
    });
  }

  public setUserAge(value: number): void | false {
    if (!this.isAllowedTracking) return false;
    if (!validateCustomUserProfile('int', 'age', value)) return false;

    return this._setUserProfile({ type: 'int', key: STANDARD_USER_PROFILE_NAME('age'), value });
  }

  public linkLineID(lineUserId: string, lineChannelId: string): void | false {
    if (!this.isAllowedTracking) return false;

    if (lineUserId == null || lineChannelId == null || lineUserId === '' || lineChannelId === '') {
      ReproConsole.error('Aborting linkLineID: lineUserId and lineChannelId are required and cannot be empty.');
      return false;
    }

    if (!this.userAnnotation) {
      ReproConsole.error('Aborting linkLineID: setUserID must be called before linkLineID.');
      return false;
    }

    const event = buildStandardEvent(
      'LINK_LINE_ID',
      {
        [STANDARD_EVENT_PROPERTY_KEYS.LINE_PROVIDED_USER_ID.name]: lineUserId,
        [STANDARD_EVENT_PROPERTY_KEYS.LINE_PROVIDED_CHANNEL_ID.name]: lineChannelId,
      },
      {}
    );
    if (!event) return false;

    this._trackEvent(event);

    this.eventChunkUploader.flushEvents('linkLineID');
  }

  public unlinkLineID(lineUserId: string, lineChannelId: string): void | false {
    if (!this.isAllowedTracking) return false;

    if (lineUserId == null || lineChannelId == null || lineUserId === '' || lineChannelId === '') {
      ReproConsole.error('Aborting unlinkLineID: lineUserId and lineChannelId are required and cannot be empty.');
      return false;
    }

    const event = buildStandardEvent(
      'UNLINK_LINE_ID',
      {
        [STANDARD_EVENT_PROPERTY_KEYS.LINE_PROVIDED_USER_ID.name]: lineUserId,
        [STANDARD_EVENT_PROPERTY_KEYS.LINE_PROVIDED_CHANNEL_ID.name]: lineChannelId,
      },
      {}
    );
    if (!event) return false;

    this._trackEvent(event);

    this.eventChunkUploader.flushEvents('unlinkLineID');
  }

  private _setUserProfile({ type, key, value }: UserProfile): void | false {
    const normalizedKey = UserProfileKeyNormalizer.process(key);

    if (!this.configured) {
      this.profileQueue.push({ type, key: normalizedKey, value });
      return false;
    }
    return this._executeSetUserProfile({ type, key: normalizedKey, value });
  }

  private _executeSetUserProfile({ type, key, value }: UserProfile): void {
    this.eventChunkUploader.setUserProfile({ type, key, value });

    // 変化がなくてもeventは発生させる
    this._trackEvent({
      name: STANDARD_EVENT_NAME('set_user_profile'),
      properties: { type, key, value },
      tracked_at: new Date(),
      metadata: {},
    });
  }

  private _validateUserId(userId: string): boolean {
    if (typeof userId !== 'string') {
      ReproConsole.error("Didn't set userId: userId can only be String.");
      return false;
    }
    if (INVALID_STRING_VALUES.includes(userId)) {
      ReproConsole.error(`Didn't set userId: ${userId} is not a valid user id value.`);
      return false;
    }
    if (!userId || [...userId.toString()].length === 0) {
      ReproConsole.error("Didn't set userId: userId can't be blank.");
      return false;
    }
    if ([...userId.toString()].length > USER_ID_LENGTH_MAX) {
      ReproConsole.error(
        `Didn't set userId: userId can only be ${USER_ID_LENGTH_MAX} characters or less; your current userId is "${userId}".`
      );
      return false;
    }
    return true;
  }

  public _generateLinkerParameters(): string {
    return [this.idfv, storage.get(LAST_EVENT_TRACKED_AT_STORAGE_KEY), this.session.isFirstVisit() ? 1 : 0].join(',');
  }

  public updateLinkerParameters(): void | false {
    this._crossDomainHandler.addLinkerQueryParameterToAnchors(document.querySelectorAll('a'));
  }

  // TODO: Logger の役割は Tracker と分離できる概念なので切り離したい
  public _log(level: 'warn' | 'error', error: Error | string): void | false {
    try {
      switch (level) {
        case 'warn':
          ReproConsole.warn(error);
          break;
        case 'error':
          ReproConsole.error(error);
          break;
        default:
          // 例外を投げてもいいかもしれない
          return false;
      }

      ErrorNotifier.notify({ error, extraPayload: this.eventChunkUploader.toJSON });
    } catch (err) {}
  }

  public async _handlePageChange(pagechangeMode: SpaMode) {
    if (!this.isAllowedTracking) return;

    if (this.spaMode === pagechangeMode) {
      this._messageManager.handlePagechange();
      try {
        this.hooks.emit(HookType.PAGECHANGE);
      } catch (error: any) {
        this._log('warn', error);
      }
    } else if (this.isInternalLinkClicked(pagechangeMode) && !this.options.close_messages_by_in_page_link) {
      // Nothing to do for in-page anchor link
    } else if (typeof history.pushState === 'function' && history.state !== undefined) {
      // Default behavior for backcompat.
      try {
        this._messageManager.hideVisibleMessages();
        this._trackByTrackingRules();
      } catch (error: any) {
        this._log('warn', error);
      }
    }
  }

  private isInternalLinkClicked(pagechangeMode: SpaMode) {
    return this.spaMode !== SpaMode.HASH && pagechangeMode === SpaMode.HASH;
  }

  private get userIdentifier(): string {
    return this.userAnnotation !== '' ? this.userAnnotation : this.idfv;
  }

  // BFCacheから復元された際に config を取得しなおし、戻る以前のページで実行したイベント
  // などによるセグメンテーションの更新とメッセージの配信が反映されるようにする
  // ref. https://web.dev/bfcache/
  private _attachPageShowEvent(): void {
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      this.reconfigure().then(() => {
        if (DefaultTracker.isInitalizedOnSpaSite) this._trackByTrackingRules();
      });
    });
  }

  private initializeGoogleAnalyticsForPrecacher(config: Configuration) {
    if (typeof gtag === 'function') {
      gtag('config', config.googleAnalyticsTrackingCode);
    } else if (typeof ga === 'function') {
      ga('create', config.googleAnalyticsTrackingCode, 'auto', 'repro');
    }
  }
}
